/**
* In this file contains the Header screen UI designs and login Header functionality
**/
import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ApiService from './../../services/ApiService';
import { generateSharedKey, generatePrivateKey, convertS3privateToPublic } from './../../services/UtilsService';
import Socket from './../../services/SocketService';
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment';
import 'moment/locale/ta'; // Import Tamil locale
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

function Header() {

	//To initialize the useState variables
	const navigate = useNavigate();
	const currentUrl = window.location.href;
	const [totalCounts, setTotalCounts] = useState({});
	const profileData = useSelector(state => state?.profile);
	const dispatch = useDispatch();
	const [notificationList, setNotificationList] = useState([]);
	const [decryptedHistory, setDecryptedHistory] = useState([]);
	const [user, setUser] = useState('');
	const privateKey = localStorage.getItem('privateKey');
	const role = Number(localStorage.getItem('role'));
	const [language, setLanguage] = useState(i18n.language || 'en'); // default
	const { t } = useTranslation();
	const currentLang = localStorage.getItem('i18nextLng');
	moment.locale(currentLang === 'ta' ? 'ta' : 'en');

	// Component Initialize function
	useEffect(() => {
		(async () => {			

			// Determine the user path based on role
			var path = parseInt(role) === 1
				? 'parent'
				: parseInt(role) === 2
					? 'physician'
					: parseInt(role) === 4
						? 'hospital/admin' // Added hospital admin path for role 4
						: 'admin';

			setUser(path);

			// Check if the current URL contains a specific path
			const url = window.location.href;

			if (url.includes('/physician/')) {
				const response = await ApiService.getNotificationCountList();
				if (response.status === 200) {
					setTotalCounts(response.data.items);
				}
			} else if (url.includes('/parent/')) {
				const response = await ApiService.getNotificationCountList();
				if (response.status === 200) {
					setTotalCounts(response.data.items);
				}
			} else if (url.includes('/hospital/admin/')) {
				const response = await ApiService.getNotificationCountList();
				if (response.status === 200) {
					setTotalCounts(response.data.items);
				}
			}
		})();
	}, [role,user]);


	//Component Initialize function
	useEffect(() => {
		/** 
		* Get the Unread Message Count
		* @data we get the Un read Message count value in Object format like {count:'count value',view:'boolean(false)'}
		**/
		Socket.getChatMsgCount(async (data) => {
			if (Object.keys(data).length !== 0) {
				var chatCount = document.getElementById('MsgCounts');
				if (chatCount)
					chatCount['innerHTML'] = parseInt(data['count']) === 0 ? '' : data['count'];
			}
		});

		/** 
		* Get the Unread Notification count
		* @data we get the Un read Notification count value in Object format like {count:'count value'}
		**/
		Socket.getNotificationCount(async (data) => {
			if (Object.keys(data).length !== 0 && data != null) {
				var notificationCount = document.getElementById('NgCounts');
				if (notificationCount)
					notificationCount['innerHTML'] = parseInt(data['count']) === 0 ? '' : data['count'];
			}
		});
	});

	/** 
	* To open the Notification popup 
	* @e we get the onClick Events
	**/
	const openNotification = async (e) => {
		e.preventDefault();
		var list = document.getElementById('notifyPopup');
		if (list) {
			if (window.location.href.includes('notification')) {
				list.classList.remove('show');
			} else {
				if (list.classList.contains('show')) {
					const response = await ApiService.getNotification({ page: 0, limit: 4, view: 'Unread' });
					if (response.status === 200) {
						setNotificationList(response.data);
					}
				}
			}
		}
	};

	/**
	* To navigate to member profile
	* @member we pass the member uuid value
	**/
	const navigateMember = (module) => {
		if (module === 'Appointment') {
			navigate('/physician/appointments');
		} else if (module === 'Vaccination') {
			navigate('/physician/vaccination');
		} else if (module === 'Development Milestones') {
			navigate('/physician/development');
		}
		//navigate('/physician/member-profile/'+member);
	}

	/**
	* To view notification
	* @e we get the onClick Events
	* @data we pass the Notification record id and member id like  {id:recordid,member:memberid}
	**/
	const handleView = async (e, data) => {
		e.preventDefault();
		const response = await ApiService.viewNotification(data.id);
		if (response.status === 200) {
			if (data.module !== 'Diagnosis') {
				navigateMember(data.module);
			} else {
				navigate('/physician/member-profile/' + data.member);
			}

		}
	};

	/* To hide or unhide sidebar */
	const togglebar = () => {
		var list = document.getElementsByTagName('body')[0];
		if (list.classList.length) {
			list.classList.remove('toggle-sidebar');
		} else {
			list.classList.add('toggle-sidebar');
		}
	};

	/** 
	* To open chat notification 
	* @e we get the onClick Events
	**/
	const openChatMessage = async (e) => {
		e.preventDefault();
		var list = document.getElementById('chatPopup');
		if (list) {
			if (window.location.href.includes('messages')) {
				list.classList.remove('show');
			} else {
				if (list.classList.contains('show')) {
					const response = await ApiService.getChatNotificationMessage();
					if (response.status === 200) {
						if (response.data['count']) {
							await setTotalCounts({ ...totalCounts, message: response.data['count'] });
						}
						if (response.data['items'].length !== 0) {
							const message = response.data['items'];
							const decryptedHistoryValue = await Promise.all(
								message.map(async (msg) => {
									const sKey = await generateSharedKey(privateKey, msg.publicKey);
									const decryptedMsg = generatePrivateKey(sKey, msg.message);
									return { ...msg, decryptedMessage: decryptedMsg };
								})
							);
							setDecryptedHistory((decryptedHistory) => [
								...decryptedHistoryValue.slice(0, 3),
							]);
						} else {
							await setDecryptedHistory([]);
						}
					} else {
						console.log('Error', response);
					}
				}
			}
		}
	}

	/** 
	* To Remove the chat notification or update its status
	* @parentId we pass the parent uuid value
	**/
	const handleNotificationChat = (parentId) => {
		sessionStorage.setItem('user', parentId);
		navigate('/physician/messages');
		const updatedHistory = decryptedHistory.filter(item => item.parent_id !== parentId);
		setDecryptedHistory(updatedHistory);
	};

	//Logout function
	const logout = () => {
		Socket.disconnect();
		navigate('/');
		localStorage.clear();
		dispatch({ type: "RESET" });
	};

	const handleLanguageChange = async (lang) => {
	try {
		const res = await ApiService.updateLanguagePreference({language: lang});
		if (res.status === 200) {
			i18n.changeLanguage(lang);
			setLanguage(lang); // trigger re-render
		}
	} catch (error) {
		console.error("Failed to update language", error);
	}
	};


	return (
		<header id="header" className="header fixed-top d-flex align-items-center">
			{/*  Logo */}
			<div className="d-flex align-items-center justify-content-between">
				<Link to={'/' + user + '/dashboard'} className="logo d-flex align-items-center">
					<img src="/assets/img/nurture-logo.svg" alt="logo" />
				</Link>
				<i onClick={togglebar} className="iconly-Curved-Category toggle-sidebar-btn"></i>
			</div>
			{/* End Logo */}

			<nav className={`header-nav ms-auto ${role===4 ? 'admin-header-nav' : ''}`}>
				<ul className="d-flex align-items-center">
					{/* Notification */}
					<li className="nav-item dropdown">
						{user && user === 'physician'
							&& <span onClick={openNotification} data-bs-toggle="dropdown" className="nav-link nav-icon">
								<i className="iconly-Bold-Notification"></i>
								<span id="NgCounts" className="badge bg-tmBlue badge-number">{totalCounts?.notification !== '0' ? totalCounts?.notification : ''}</span>
							</span>}
						{user && user === 'parent'
							&& <span onClick={openNotification} data-bs-toggle="dropdown" className="nav-link nav-icon">
								<i className="iconly-Bold-Notification"></i>
								<span id="NgCounts" className="badge bg-tmBlue badge-number">{totalCounts?.notification !== '0' ? totalCounts?.notification : ''}</span>
							</span>}
						{user && user.includes('hospital/admin')
							&& <span onClick={openNotification} data-bs-toggle="dropdown" className="nav-link admin-nav-icon">
								<i className="iconly-Bold-Notification"></i>
								<span id="NgCounts" className="badge badge-number">{totalCounts?.notification !== '0' ? totalCounts?.notification : ''}</span>
							</span>
						}
						<ul id="notifyPopup" className="dropdown-menu dropdown-menu-end dropdown-menu-arrow notifications">
							<li className="dropdown-header">
							{/* <li className={`${role === 4 ? 'admin-dropdown-header' : 'dropdown-header'}`}> */}
								{t('common.youHave')} {notificationList.count ? notificationList.count : '0'} {t('common.unreadNotification')}
								<Link to={currentUrl.includes('/parent/') ? '/parent/notification' : currentUrl.includes('/physician/') ? '/physician/notification' : '/hospital/admin/notification'}>
									<span className={`badge rounded-pill p-2 ms-2 ${role===4 ? 'bg-tmAquaDgreen' : 'bg-tmBlue'}`}>{t('common.viewAll')}</span>
								</Link>
							</li>
							<li><hr className="dropdown-divider" /></li>
							{notificationList && notificationList.items && notificationList.items.length > 0 ? (
								<div className={`${role === 4 ? 'admin-overflow-handel' : 'overflow-handel'}`}
								>{notificationList.items.map((item, i) => (
									<React.Fragment key={i}>
										<li className="notification-item " onClick={(e) => handleView(e, { id: item.id, member: item.member, mapping: item.mapping, module: item.module })}>
											<i className="iconly-Light-InfoSquare vFlip text-warning"></i>
											<div>
												<h4 className="text-capitalize">{currentLang === 'ta' ? item.module_tamil || item.module : item.module} {t('sidebar.notification')}</h4>
												<p>{currentLang === 'ta' ? item.message_tamil || item.message : item.message} </p>
												<p><small>{moment(item.createdAt).fromNow()}</small></p>
											</div>
										</li>
										<li><hr className="dropdown-divider" /></li>
									</React.Fragment>
								))}</div>
							) : (
								<li className="text-center">{t('common.noUnreadNotification')}</li>
							)}
							<li className={`${role === 4 ? 'admin-dropdown-footer' : 'dropdown-footer'}`}>
								<Link to={currentUrl.includes('/parent/') ? '/parent/notification' : currentUrl.includes('/physician/') ? '/physician/notification' : '/hospital/admin/notification'}>{t('common.showAllNotification')}</Link>
							</li>
						</ul>
						{/* End Notification Dropdown Items */}

					</li>
					{/* Chat Messages */}
					{/* <li className="nav-item dropdown" style={{cursor:'pointer'}}>
					{user && user==='physician'  && 
					<span className="nav-link nav-icon" onClick={openChatMessage} data-bs-toggle="dropdown">
						<i className="iconly-Bold-Chat"></i>
						<span id="MsgCounts" className="badge bg-tmBlue badge-number">{totalCounts?.message !=='0'?totalCounts?.message:''}</span>
					</span>}
					{user && user==='parent'  && 
					<Link to='/parent/messages' className="nav-link nav-icon">
						<i className="iconly-Bold-Chat"></i>
						<span id="MsgCounts" className="badge bg-tmBlue badge-number">{totalCounts?.message !=='0'?totalCounts?.message:''}</span>
					</Link>}
					<ul id="chatPopup" className="dropdown-menu dropdown-menu-end dropdown-menu-arrow messages">
						<li className="dropdown-header">
							You have <span id="MsgCounts">{totalCounts?.message !=='0'?totalCounts?.message:'0'}</span> new messages
							<Link to='/physician/messages'><span className="badge rounded-pill bg-tmBlue p-2 ms-2">{constantVariables.common.viewAll}</span></Link>
						</li>
						<li> <hr className="dropdown-divider"/></li>
						{decryptedHistory && decryptedHistory.length !==0 ? (
						  <>
						    <div className="overflow-handel">{decryptedHistory.map((item,i) => (
							   <React.Fragment key={i} >
								<li className="message-item" >
								  <Link to="/physician/messages" onClick={() => handleNotificationChat(item.created_by)}>
									{item?.photo && (item?.photo==='default.png' ? <img src="/assets/img/user-avatar.png" style={{width:"40px",height:"40px",borderRadius:"50%",marginRight:"0.5rem"}} alt="Profile"/> : <img src={convertS3privateToPublic(item?.photo)} style={{width:"40px",height:"40px",borderRadius:"50%",marginRight:"0.5rem"}} alt="Profile"/>)}
									  <div>
										<h4>{item.fullName}</h4>
										<p>{item.decryptedMessage}</p>
										<p>{moment(item.createdAt).fromNow()}</p>
									  </div>
								  </Link>
								</li>
								<li> <hr className="dropdown-divider"/></li>
							  </React.Fragment>
						    ))}</div>
						  </>
						 ):(
							<li className="text-center">No unread messages available</li>
						)}
												
						{currentUrl.includes('/physician/') && !currentUrl.includes('/parent/') &&
							<li className="dropdown-footer">
								<Link to="/physician/messages">Show all messages</Link>
							</li>
						}
					</ul>
				</li>  */}
					{/*<!-- Profile Dropdown -->*/}
					<li className="nav-item d-flex dropdown pe-3">
						  {/* <a
							className="nav-link nav-icon d-flex align-items-center pe-0"
							href="javascript:;"
							data-bs-toggle="dropdown"
						>
							<img
							src={`https://flagcdn.com/24x18/${i18n.language === 'ta' ? 'in' : 'us'}.png`}
							alt="Lang"
							className="me-2"
							width="24"
							height="18"
							/>
						</a>

						<ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow">
							<li>
							<button
								className="dropdown-item d-flex align-items-center"
								onClick={() => handleLanguageChange('en')}
							>
								<img src="https://flagcdn.com/20x15/us.png" alt="English" width="20" className="me-2" />
								<span>English</span>
							</button>
							</li>
							<li>
							<button
								className="dropdown-item d-flex align-items-center"
								onClick={() => handleLanguageChange('ta')}
							>
								<img src="https://flagcdn.com/20x15/in.png" alt="Tamil" width="20" className="me-2" />
								<span>தமிழ்</span>
							</button>
							</li>
						</ul> */}
						{/*<!-- Profile picture -->*/}
						<a className="nav-link nav-profile d-flex align-items-center pe-0" href="javascript;" data-bs-toggle="dropdown">
							{profileData && profileData?.photo === 'default.png' && <img src="/assets/img/user-avatar.png" alt="Profile" className="rounded-circle me-2" />}
							{profileData && profileData?.photo !== 'default.png' && <img src={convertS3privateToPublic(profileData?.photo)} alt="Profile" className="rounded-circle me-2" />}
							<h6 className="small mb-0"><span className="fw-bold text-capitalize">{profileData ? profileData.first_name + ' ' + profileData.last_name : ''}</span></h6>
						</a>

						<ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow profile">
							{/*<!-- Profile name and email -->*/}
							<li className="dropdown-header">
								<h6 className="text-capitalize">{profileData?.first_name + ' ' + profileData?.last_name}</h6>
								<span>{profileData?.email}</span>
							</li>
							<li> <hr className="dropdown-divider" /></li>
							{/*<!-- My Profile -->*/}
							<Link to={'/' + user + '/profile'} className="dropdown-item d-flex align-items-center">
								<i className="iconly-Light-User"></i>
								<span>{t('header.myProfile')}</span>
							</Link>
							<hr className="dropdown-divider" />
							{/*<!-- Profile settings -->*/}
							{user && user !== 'physician' && user !== 'parent' &&
								<>
									<Link to={'/' + user + '/settings'} className="dropdown-item d-flex align-items-center">
										<i className="iconly-Light-Setting"></i>
										<span>{t('header.accountSettings')}</span>
									</Link>
									<hr className="dropdown-divider" />
								</>
							}
							{/*<!-- Profile Logout -->*/}
							<li>
								<span onClick={logout} className="dropdown-item d-flex align-items-center">
									<i className="iconly-Light-Logout"></i>
									<span >{t('sidebar.logout')}</span>
								</span>
							</li>
						</ul>
					</li>
					{/*<!-- Profile Dropdown End -->*/}
				</ul>
			</nav>
		</header>
	);
}

export default Header;
